import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi } from "./api";

interface RevealableFieldProps {
  clientId: string;
  field: "ssn" | "dob";
  maskedValue?: string | null;
  // How long the value stays revealed before auto-hiding.
  autoHideMs?: number;
  className?: string;
}

/**
 * RevealableField — renders the masked value plus an eye icon.
 * Click the icon → calls /api/clients/:id/reveal which:
 *   - decrypts the value server-side
 *   - writes an events row of kind='pii.revealed'
 *   - returns the cleartext value
 * Auto-hides after 30s or when the host element loses focus.
 */
export function RevealableField({
  clientId,
  field,
  maskedValue,
  autoHideMs = 30_000,
  className,
}: RevealableFieldProps) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const fallbackMask = field === "ssn" ? "XXX-XX-••••" : "XX/XX/••••";
  const masked = maskedValue || fallbackMask;

  function hide() {
    setRevealed(null);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  async function reveal() {
    if (revealed) {
      hide();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await clientsApi.revealPii(clientId, field);
      setRevealed(res.value);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(hide, autoHideMs);
    } catch (e: any) {
      setError(e?.message || "Could not reveal");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <span
      className={`revealable-field ${className || ""}`}
      onBlur={hide}
      tabIndex={-1}
    >
      <span className="revealable-value">{revealed || masked}</span>
      <button
        type="button"
        className="revealable-toggle"
        onClick={reveal}
        title={revealed ? "Hide" : `Reveal ${field.toUpperCase()}`}
        disabled={loading}
      >
        {loading ? (
          <Loader2 size={14} className="spin" />
        ) : revealed ? (
          <EyeOff size={14} />
        ) : (
          <Eye size={14} />
        )}
      </button>
      {error && <span className="revealable-error">{error}</span>}
    </span>
  );
}
